import { Component } from 'react';
import type { ErrorInfo, ReactNode } from 'react';

import { logger, notify } from '@/shared';

interface RootErrorBoundaryProps {
  children: ReactNode;
}

interface RootErrorBoundaryState {
  hasError: boolean;
  errorMessage: string | null;
}

/**
 * Root error boundary that catches unhandled React errors.
 * Displays a fallback UI and logs errors via the structured logger.
 *
 * Note: Error boundaries do not catch errors in:
 * - Event handlers
 * - Async code (setTimeout, promises)
 * - Server-side rendering
 * - Errors thrown in the boundary itself
 *
 * Use installGlobalErrorHandlers for those cases.
 */
export class RootErrorBoundary extends Component<RootErrorBoundaryProps, RootErrorBoundaryState> {
  constructor(props: RootErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, errorMessage: null };
  }

  static getDerivedStateFromError(error: unknown): RootErrorBoundaryState {
    // Extract a safe message (no PHI should be in Error messages by convention)
    const errorMessage = error instanceof Error ? error.message : 'An unexpected error occurred';

    return { hasError: true, errorMessage };
  }

  componentDidCatch(error: unknown, errorInfo: ErrorInfo): void {
    // Log to structured logger (no PHI in error payloads by convention)
    logger.error(
      'React error boundary caught error',
      {
        surface: 'react-render',
        operation: 'root.errorBoundary',
        componentStack: errorInfo.componentStack ?? undefined,
      },
      error
    );

    notify.error('Something went wrong', error);
  }

  handleReset = (): void => {
    this.setState({ hasError: false, errorMessage: null });
  };

  render(): ReactNode {
    if (this.state.hasError) {
      return (
        <div
          role="alert"
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '100vh',
            padding: '2rem',
            textAlign: 'center',
            fontFamily: 'system-ui, sans-serif',
          }}
        >
          <h1 style={{ marginBottom: '1rem', fontSize: '1.5rem' }}>Something went wrong</h1>
          <p
            style={{
              marginBottom: '1.5rem',
              color: '#666',
              maxWidth: '400px',
            }}
          >
            We encountered an unexpected error. Please try again, or contact support if the problem
            persists.
          </p>
          <button
            type="button"
            onClick={this.handleReset}
            style={{
              padding: '0.75rem 1.5rem',
              fontSize: '1rem',
              backgroundColor: '#0066cc',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
            }}
          >
            Try Again
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}
